<template>
  <el-dialog
    :model-value="modelValue"
    :title="dialogTitle"
    class="operation-help-dialog"
    append-to-body
    destroy-on-close
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="operation-help-body">
      <el-alert
        v-if="document.summary"
        :title="document.summary"
        type="info"
        :closable="false"
        show-icon
        class="operation-help-summary"
      />

      <section v-if="document.flow && document.flow.length" class="operation-help-section">
        <h3>操作流程</h3>
        <div class="operation-help-flow" role="list" :aria-label="dialogTitle + '操作流程'">
          <template v-for="(item, index) in document.flow" :key="`${index}-${item}`">
            <span class="operation-help-node" role="listitem">{{ item }}</span>
            <el-icon v-if="index < document.flow.length - 1" class="operation-help-arrow"><ArrowRight /></el-icon>
          </template>
        </div>
      </section>

      <section v-if="document.steps && document.steps.length" class="operation-help-section">
        <h3>操作步骤</h3>
        <el-timeline class="operation-help-timeline">
          <el-timeline-item
            v-for="(step, index) in document.steps"
            :key="`${index}-${step.title}`"
            :timestamp="step.label || `步骤 ${index + 1}`"
            placement="top"
          >
            <div class="operation-help-step">
              <h4>{{ step.title }}</h4>
              <p>{{ step.content }}</p>
            </div>
          </el-timeline-item>
        </el-timeline>
      </section>

      <section
        v-for="(section, index) in document.sections || []"
        :key="`${index}-${section.title}`"
        class="operation-help-section operation-help-custom-section"
      >
        <h3>{{ section.title }}</h3>
        <p v-if="section.content">{{ section.content }}</p>
        <div
          v-if="section.flow && section.flow.length"
          class="operation-help-flow operation-help-custom-flow"
          role="list"
          :aria-label="section.title + '操作流程'"
        >
          <template v-for="(item, flowIndex) in section.flow" :key="`${flowIndex}-${item}`">
            <span class="operation-help-node" role="listitem">{{ item }}</span>
            <el-icon v-if="flowIndex < section.flow.length - 1" class="operation-help-arrow"><ArrowRight /></el-icon>
          </template>
        </div>
        <el-timeline v-if="section.steps && section.steps.length" class="operation-help-timeline operation-help-custom-timeline">
          <el-timeline-item
            v-for="(step, stepIndex) in section.steps"
            :key="`${stepIndex}-${step.title}`"
            :timestamp="step.label || `步骤 ${stepIndex + 1}`"
            placement="top"
          >
            <div class="operation-help-step">
              <h4>{{ step.title }}</h4>
              <p>{{ step.content }}</p>
            </div>
          </el-timeline-item>
        </el-timeline>
        <ul v-if="section.items && section.items.length">
          <li v-for="item in section.items" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section v-if="document.reminders && document.reminders.length" class="operation-help-section operation-help-reminders">
        <h3>{{ document.remindersTitle || "关键提醒" }}</h3>
        <el-alert
          v-for="item in document.reminders"
          :key="item"
          :title="item"
          :type="document.reminderType || 'warning'"
          :closable="false"
          show-icon
        />
      </section>
    </div>

    <template #footer>
      <el-button type="primary" @click="$emit('update:modelValue', false)">{{ closeText }}</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { createEmptyHelpDocument } from "./helpRegistry";

export default {
  name: "OperationHelpDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    document: { type: Object, default: () => createEmptyHelpDocument() },
    titleSuffix: { type: String, default: "操作帮助" },
    closeText: { type: String, default: "我知道了" }
  },
  emits: ["update:modelValue"],
  computed: {
    dialogTitle() {
      if (this.document.dialogTitle) return this.document.dialogTitle;
      return `${this.document.title || this.document.name || "页面"}${this.titleSuffix}`;
    }
  }
};
</script>

<style>
.operation-help-dialog { width: 92%; max-width: 900px; }
.operation-help-body { max-height: 68vh; overflow-y: auto; padding: 0 4px 8px 0; }
.operation-help-summary { margin-bottom: 22px; }
.operation-help-section + .operation-help-section { margin-top: 24px; }
.operation-help-section > h3 {
  margin: 0 0 14px;
  color: var(--el-text-color-primary);
  font-size: 16px;
}
.operation-help-flow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 14px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-fill-color-lighter);
}
.operation-help-node {
  padding: 7px 11px;
  border: 1px solid var(--el-color-primary-light-5);
  border-radius: 16px;
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary-dark-2);
  font-size: 13px;
  line-height: 18px;
}
.operation-help-arrow { flex: 0 0 auto; color: var(--el-text-color-secondary); }
.operation-help-timeline { padding-left: 4px; }
.operation-help-step {
  padding: 12px 14px;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 6px;
  background: var(--el-bg-color-page);
}
.operation-help-step h4 { margin: 0 0 6px; color: var(--el-text-color-primary); font-size: 14px; }
.operation-help-step p,
.operation-help-custom-section p,
.operation-help-custom-section li { margin: 0; color: var(--el-text-color-regular); line-height: 1.7; }
.operation-help-custom-section > p { margin-bottom: 14px; }
.operation-help-custom-flow { margin-bottom: 16px; }
.operation-help-custom-timeline { margin-top: 16px; }
.operation-help-custom-section ul { margin: 0; padding-left: 22px; }
.operation-help-reminders { display: flex; flex-direction: column; gap: 10px; }
.operation-help-reminders > h3 { margin-bottom: 4px; }

@media (max-width: 600px) {
  .operation-help-body { max-height: 72vh; }
  .operation-help-flow { align-items: stretch; flex-direction: column; }
  .operation-help-arrow { align-self: center; transform: rotate(90deg); }
}
</style>
